import { ExpandMore } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Divider } from '@mui/material'
import dayjs from 'dayjs'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import Button from '../../components/actions/Button'
import ModeSwitch, { ModeOption } from '../../components/ModeSwitch'
import useUser from '../../contexts/hooks/useUser'
import { ObjectContext } from '../../contexts/objectContext/ObjectContext'
import { UserContext } from '../../contexts/UserContext'
import { useContract } from '../../hooks/useContract'
import PageWrapper from '../../wrappers/pageWrapper/PageWrapper'
import PendingContracts from './components/PendingContracts'
import { useOldContracts } from './hooks/useOldContracts'
import ContractBilling from './pages/ContractBilling'
import ContractDetailsPage from './pages/ContractDetails'
import ContractOptionsPage from './pages/ContractOptions'

/**
 * Type definition for the modes of ContractInformation component.
 */
export type ContractInformationMode = 'contract' | 'options' | 'billing'

export interface ContractInformationProps {
	initialMode?: 'options' | 'contract' | 'billing'
}

/**
 * ContractInformation component displays contract details
 * or contract options based on the selected mode.
 */
const ContractInformation: React.FC<ContractInformationProps> = ({ initialMode }) => {
	const { synerallContractId } = useContext(ObjectContext)
	const { isAnonymousUser } = useContext(UserContext)
	const location = useLocation<{ initialMode?: ContractInformationMode; backPath?: string }>()

	const { t } = useTranslation('pages_contractInformation')

	const [currentMode, setCurrentMode] = useState<ContractInformationMode>(
		location.state?.initialMode ?? initialMode ?? 'contract'
	)
	const [fetchOldContracts, setFetchOldContracts] = useState(false)

	const { user } = useUser()
	const { oldContractsData, refetch } = useOldContracts(fetchOldContracts)

	const { contractData, isContractLoading } = useContract()

	const modeOptions: ModeOption<ContractInformationMode>[] = [
		{ mode: 'contract', label: t('sopimus'), rendered: Boolean(user) },
		{ mode: 'billing', label: t('billing.contactInformation'), rendered: Boolean(user) },
		{ mode: 'options', label: t('asetukset'), rendered: Boolean(user) },
	]

	const handleFetchOldContracts = async () => {
		setFetchOldContracts(true)
		await refetch()
	}

	useEffect(() => {
		if ((oldContractsData?.length ?? 0) > 0) {
			setFetchOldContracts(true)
		}
	}, [oldContractsData])

	return (
		<PageWrapper
			page='contract'
			appBarOptions={location.state?.backPath ? { backPath: location.state.backPath } : undefined}
		>
			<div id='contract-information'>
				<ModeSwitch<ContractInformationMode>
					options={modeOptions}
					onModeChange={setCurrentMode}
					currentMode={currentMode}
				/>
				{currentMode === 'contract' && (
					<>
						<ContractDetailsPage contract={contractData} contractLoading={isContractLoading} />

						<PendingContracts />

						{/* Old Contracts Section */}
						{!isAnonymousUser && !!synerallContractId && (
							<div className='container' style={{ marginBottom: '12px' }}>
								<p className='header font-semibold text-md text-text-dark dark:text-white'>
									{t('oldContracts')}
								</p>
								<p className='text text-text-grey-200 dark:text-text-grey-100'>
									{t('oldContractsDescription')}
								</p>

								{(oldContractsData?.length ?? 0) > 0 &&
									oldContractsData?.map(contract => {
										if (!contract) return null

										// Get the product name from the contract
										const productName = contract.Products?.[0]?.ProductNames.find(
											product => product.Lang === 'FI'
										)
											?.Value.split(' ')
											.slice(0, 2)
											.join(' ')
											.trim()
											.replaceAll('-', '')

										// Find address from objects array
										const addressObj = contract.Objects?.find(obj => obj?.Address?.StreetAddress)
										const address = addressObj?.Address?.StreetAddress

										return (
											<div key={contract.Id} className='container text-text-dark dark:text-white'>
												{address && (
													<>
														<div className='info-container'>
															<div className='child'>
																<p className='text'>{address}</p>
															</div>
														</div>
														<Divider />
													</>
												)}
												<div className='info-container'>
													<div className='info !mt-0'>
														<p className='name'>{productName}</p>
														<p className='description text-text-grey-200 dark:text-text-grey-100'>
															{t('fossiilivapaa')}
														</p>
													</div>
												</div>

												<Divider />

												<div className='info'>
													<div className='info-container2'>
														<div className='child'>
															<p className='header'>{t('alkamispaiva')}</p>
															<p className='text text-text-grey-200 dark:text-text-grey-100'>
																{contract.StartDate
																	? dayjs(contract.StartDate).format('DD.MM.YYYY')
																	: ''}
															</p>
														</div>
													</div>
													<div className='info-container2'>
														<div className='child'>
															<p className='header'>{t('loppumispaiva')}</p>
															<p className='text text-text-grey-200 dark:text-text-grey-100'>
																{contract.EndDate
																	? dayjs(contract.EndDate).format('DD.MM.YYYY')
																	: ''}
															</p>
														</div>
													</div>
												</div>

												{contract.Prices && contract.Prices.length > 0 && (
													<>
														<Divider />

														<Accordion>
															<AccordionSummary expandIcon={<ExpandMore />}>
																<p className='header'>{t('priceInfo')}</p>
															</AccordionSummary>
															<AccordionDetails>
																<div className='prices-list'>
																	{contract.Prices.map((price, priceIndex) => {
																		if (!price) return null

																		const userLang = user?.language?.toUpperCase() || 'FI'
																		const componentName =
																			price.ComponentNames?.find(name => name.Lang === userLang)
																				?.Value ||
																			price.ComponentNames?.find(name => name.Lang === 'FI')
																				?.Value ||
																			price.ComponentNames?.[0]?.Value ||
																			price.ComponentType

																		const priceUnit =
																			price.PriceUnits?.find(unit => unit.Lang === userLang)
																				?.Value ||
																			price.PriceUnits?.find(unit => unit.Lang === 'FI')?.Value ||
																			price.PriceUnits?.[0]?.Value ||
																			price.Unit

																		return (
																			<React.Fragment key={`${contract.Id}-price-${priceIndex}`}>
																				<div className='price-item'>
																					<div className='price-header'>
																						<p className='price-type'>{componentName}</p>
																					</div>
																					<div className='price-value'>
																						<p>
																							{price.Price.toFixed(2).toString().replace('.', ',')}{' '}
																							{priceUnit}
																						</p>
																					</div>
																				</div>
																				{priceIndex < contract.Prices.length - 1 && (
																					<Divider style={{ margin: '8px 0' }} />
																				)}
																			</React.Fragment>
																		)
																	})}
																</div>
															</AccordionDetails>
														</Accordion>
													</>
												)}
											</div>
										)
									})}

								{(oldContractsData?.length ?? 0) === 0 && (
									<Button
										className='m-0 mt-2'
										onClick={handleFetchOldContracts}
										disabled={fetchOldContracts}
									>
										{t('loadOldContracts')}
									</Button>
								)}
								{fetchOldContracts && oldContractsData?.length === 0 && (
									<p className='no-old-contracts'>{t('noContractsFound')}</p>
								)}
							</div>
						)}
					</>
				)}
				{currentMode === 'billing' && <ContractBilling />}
				{currentMode === 'options' && <ContractOptionsPage />}
			</div>
		</PageWrapper>
	)
}

export default ContractInformation
